import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { pluralWord } from "@/lib/plural";

export type PlatformCounts = {
  events_ahead: number;
  circles: number;
  members: number;
  experts: number;
  organizers: number;
};
export type PlatformCountKey = keyof PlatformCounts;

const FORMS: Record<PlatformCountKey, [string, string, string]> = {
  events_ahead: ["событие впереди", "события впереди", "событий впереди"],
  circles: ["сообщество", "сообщества", "сообществ"],
  members: ["участник", "участника", "участников"],
  experts: ["эксперт", "эксперта", "экспертов"],
  organizers: ["организатор", "организатора", "организаторов"],
};

/** Порядок чисел в шапке каждого раздела. */
export const STATS_LINES = {
  events: ["events_ahead", "circles", "experts"],
  circles: ["circles", "members", "events_ahead"],
  experts: ["experts", "organizers", "circles"],
} as const satisfies Record<string, readonly PlatformCountKey[]>;

/** «N событий впереди · M сообществ · …»; нули и пропуски не показываются. */
export function formatStatsLine(
  counts: Partial<PlatformCounts> | null | undefined,
  keys: readonly PlatformCountKey[],
): string {
  if (!counts) return "";
  return keys
    .map((k) => {
      const n = Number(counts[k] ?? 0);
      if (!Number.isFinite(n) || n <= 0) return null;
      const [one, few, many] = FORMS[k];
      return `${n} ${pluralWord(n, one, few, many)}`;
    })
    .filter(Boolean)
    .join(" · ");
}

export function usePlatformCounts() {
  return useQuery({
    queryKey: ["platform-counts"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("platform_counts" as never);
      if (error) throw error;
      return (data ?? {}) as unknown as PlatformCounts;
    },
  });
}

export function useStatsLine(section: keyof typeof STATS_LINES): string {
  const { data } = usePlatformCounts();
  return formatStatsLine(data, STATS_LINES[section]);
}

/** Вход «Запросы экспертов» в афише: организатор для экспертов или есть свои события. */
export function showExpertRequestsEntry(p: {
  hostsForExperts?: boolean | null;
  ownEventsCount?: number | null;
}): boolean {
  return !!p.hostsForExperts || (p.ownEventsCount ?? 0) > 0;
}

/** Карточка «Ищешь площадку?» — только экспертам с заполненной карточкой. */
export function showExpertVenueCard(expertFieldsComplete: boolean | null | undefined): boolean {
  return expertFieldsComplete === true;
}

/** Секция «Для организаторов» в профиле раскрыта сразу, а не по ссылке «Я организую мероприятия». */
export function showHostsSectionOpen(o: {
  intents: readonly string[];
  ownEvents: number;
  hostsOn: boolean;
}): boolean {
  return (
    o.hostsOn ||
    o.ownEvents > 0 ||
    o.intents.includes("organize") ||
    o.intents.includes("community")
  );
}
